import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { isMobileContentBreakpoint } from 'helpers/HtmlHelpers';

import styles from 'styles/components/common/tooltip.scss';

class Tooltip extends Component {
  constructor(props) {
    super(props);
    this.wrapperRef = createRef();
  }

  state = {
    visible: false,
    isMobileScreen: false
  };

  componentDidUpdate() {
    const { direction } = this.props;
    const isMobileScreen = isMobileContentBreakpoint(this.wrapperRef.current);
    if (isMobileScreen !== this.state.isMobileScreen && direction !== 'bottom') {
      this.setState({ isMobileScreen });
    }
  }

  setVisibility = (visible = false) => {
    this.setState({ visible });
  };

  hide = () => {
    this.state.isMobileScreen ?
      setTimeout(this.setVisibility, 0) /* If the tooltip has a link, we must wait
                                             for the next batch of events.
                                             Otherwise the tooltip will be hidden
                                             before the link click is registered.
                                             At the time of implementing this,
                                             attempting to use requestAnimationFrame()
                                             broke dismissing the tooltip via tapping the overlay */
      : this.setVisibility(false);
  };

  makeDirectionClassName = direction => {
    switch (direction) {
      case 'left':
        return styles.left;
      case 'right':
        return styles.right;
      case 'bottom':
        return styles.bottom;
      default:
        return '';
    }
  };

  show = () => {
    this.setVisibility(true);
  };

  onMouseEnter = () => {
    const { onMouseEnter: handleMouseEnter } = this.props;
    handleMouseEnter && handleMouseEnter();
    this.show();
  };

  onMouseLeave = () => {
    const { onMouseLeave: handleMouseLeave } = this.props;
    handleMouseLeave && handleMouseLeave();
    this.hide();
  };

  makeTooltipContent = ({ contentTarget = 'default' } = {}) => {
    const {
      props: { tooltipClassName, contentClassName, content, simple, direction, sponsored },
      state: { isMobileScreen, visible },
      context: { testId }
    } = this;
    const isContentForMobile = contentTarget === 'mobile';
    const shouldRenderContents = isContentForMobile ? isMobileScreen : !isMobileScreen;

    return visible && shouldRenderContents && (
      <div
        data-test-id={testId('tooltipContent')}
        className={cn(styles.tooltip,
          {
            [styles.simpleTooltip]: simple,
            [styles.sponsored]: sponsored,
            [styles.forMobile]: isContentForMobile
          },
          this.makeDirectionClassName(direction),
          tooltipClassName)}>
        <div className={cn(contentClassName, styles.content)}>{content}</div>
      </div>
    );
  };

  render() {
    const { wrapperClassName, children } = this.props;
    const { visible } = this.state;
    return (
      <>
        <div className={cn(styles.overlay, { [styles.activateOverlay]: visible })} onMouseEnter={this.onMouseLeave}></div>
        <div
          className={cn(styles.wrapper, wrapperClassName)}
          ref={this.wrapperRef}
          onMouseOut={this.onMouseLeave}
          onBlur={this.onMouseLeave}
          onMouseOver={this.onMouseEnter}
          onFocus={this.onMouseEnter}
        >
          {children}
          {this.makeTooltipContent()}
        </div>
        <div className={styles.mobileContainer}>
          <div className={styles.mobileEncompass}>
            {this.makeTooltipContent({ contentTarget: 'mobile' })}
          </div>
        </div>
      </>
    );
  }
}

Tooltip.propTypes = {
  children: PropTypes.any.isRequired,
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element
  ]).isRequired,
  wrapperClassName: PropTypes.string,
  tooltipClassName: PropTypes.string,
  tooltipContent: PropTypes.string,
  contentClassName: PropTypes.string,
  simple: PropTypes.bool
};

Tooltip.contextTypes = {
  testId: PropTypes.func
};

export default Tooltip;
